import { saveAccumulatedTime } from '@app/application/wall-clock-time/api'
import { SfLog } from '@app/adapters/logger'

export default class WallClockTimeTracker {
  private startTime: number | null = null
  private accumulatedTime: number = 0
  private timerId: number | null = null
  private ownerId: number
  private ownerType: string
  private ledgerId: number
  private companyId: string
  private firmId: string
  private stopped: boolean
  readonly saveAccumulatedTimeAfterMs: number = 30000

  constructor(
    ownerId: number,
    ownerType: string,
    ledgerId: number,
    companyId: string,
    firmId: string
  ) {
    this.ownerId = ownerId
    this.ownerType = ownerType
    this.ledgerId = ledgerId
    this.companyId = companyId
    this.firmId = firmId
    this.stopped = true
    this.start = this.start.bind(this)
    this.teardown = this.teardown.bind(this)
    this.subscribeToEvents = this.subscribeToEvents.bind(this)
    this.unsubscribeFromEvents = this.unsubscribeFromEvents.bind(this)
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)
    this.handleFocusChange = this.handleFocusChange.bind(this)
    this.startTracking = this.startTracking.bind(this)
    this.stopTracking = this.stopTracking.bind(this)
    this.accumulateStartTime = this.accumulateStartTime.bind(this)
    this.sendAccumulatedTime = this.sendAccumulatedTime.bind(this)
    this.save = this.save.bind(this)
    this.clearTimeInterval = this.clearTimeInterval.bind(this)
  }

  start(): void {
    this.subscribeToEvents()
    this.handleVisibilityChange()
    this.handleFocusChange()
  }

  teardown = (): void => {
    this.clearTimeInterval()
    this.unsubscribeFromEvents()
  }

  subscribeToEvents(): void {
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    window.addEventListener('focus', this.handleFocusChange)
    window.addEventListener('blur', this.handleFocusChange)
    window.addEventListener('beforeunload', this.sendAccumulatedTime)
  }

  unsubscribeFromEvents(): void {
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    )
    window.removeEventListener('focus', this.handleFocusChange)
    window.removeEventListener('blur', this.handleFocusChange)
    window.removeEventListener('beforeunload', this.sendAccumulatedTime)
  }

  handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.startTracking()
    } else {
      this.stopTracking()
    }
  }

  handleFocusChange = (): void => {
    if (document.hasFocus()) {
      this.startTracking()
    } else {
      this.stopTracking()
    }
  }

  startTracking(): void {
    this.stopped = false
    if (this.startTime === null) {
      this.startTime = Date.now()
    }

    if (this.timerId === null) {
      this.timerId = window.setInterval(() => {
        this.sendAccumulatedTime()
      }, this.saveAccumulatedTimeAfterMs)
    }
  }

  stopTracking(): void {
    if (this.stopped) {
      return
    } else {
      this.stopped = true
    }
    this.clearTimeInterval()
    this.accumulateStartTime()
    if (this.accumulatedTime > this.saveAccumulatedTimeAfterMs) {
      void this.save()
    }
  }

  accumulateStartTime(): void {
    if (this.startTime !== null) {
      const currentTime = Date.now()
      this.accumulatedTime += currentTime - this.startTime
      this.startTime = null
    }
  }

  sendAccumulatedTime = (): void => {
    this.accumulateStartTime()
    this.startTime = Date.now()

    if (this.accumulatedTime > 0) {
      void this.save()
    }
  }

  save = async (): Promise<void> => {
    try {
      await saveAccumulatedTime({
        ownerId: this.ownerId,
        ownerType: this.ownerType,
        firmId: this.firmId,
        companyId: this.companyId,
        ledgerId: this.ledgerId,
        durationInSeconds: this.accumulatedTime / 1000, // convert to seconds
      })
      this.accumulatedTime = 0
    } catch (err) {
      SfLog.warn('Error updating wall clock time', {
        extra: {
          error: err,
        },
      })
      this.teardown()
    }
  }

  clearTimeInterval(): void {
    if (this.timerId !== null) {
      clearInterval(this.timerId)
      this.timerId = null
    }
  }
}
